// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { useTranslate } from "helpers";
import {
  deleteFleetDocumentAPI,
  getFleetDocumentsAPI,
} from "services/api/fleet/vehicles/documents/api";
import { getFleetTireSetAPI } from "services/api/fleet/tires/api";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  FleetDocumentRow,
  FleetDocumentSearchFormValues,
} from "services/api/fleet/vehicles/documents/types";
import { FleetTireSet } from "services/api/fleet/tires/types";
import {
  TireSetDocumentSearchPageCmp,
  TireSetDocumentSearchPageProps,
} from "./types";
import { FLEET_TIRE_SET_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Dialog } from "ui/components/Dialog";
import Badge from "ui/components/Badge/Badge";

// Moduli specifični za projekt
import { Routes as FleetTireRoutes } from "ui/modules/fleet/tires/navigation";
import TabMenu from "../TabMenu/TabMenu";
import { SIDEBAR_ITEM_FLEET_TIRE_SET_SEARCH } from "app/components/Sidebar/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import {
  getDimensionSet,
  getTireIcon,
  getTireType,
} from "services/api/fleet/tires/helpers";

// Lokalne komponente
import TireSetDocumentSearchFilters from "./components/TireSetDocumentSearchFilters/TireSetDocumentSearchFilters";
import TireSetDocumentSearchResults from "./components/TireSetDocumentSearchResults/TireSetDocumentSearchResults";

const TireSetDocumentSearchPage: TireSetDocumentSearchPageCmp = (
  props: TireSetDocumentSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [tireSet, setTireSet] = useState<FleetTireSet | null>(null);

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetDocumentRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [documentId, setDocumentId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      fetchTireSet(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);

  const fetchTireSet = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetTireSetAPI(id);
      setTireSet(result);
    } catch {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchSearchResult = async (
    id: string,
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetDocumentsAPI(pagination, name, id, "TireSet");
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setDocumentId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setDocumentId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (formValues: FleetDocumentSearchFormValues) => {
      if (id) fetchSearchResult(id, searchResults.pagination, formValues.search);
    },
    [id]
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setDocumentId(id);
    setOpenDialog(true);
  };

  const handleDocumentDeleteDialogConfirm = useCallback(async () => {
    if (!documentId) return;

    try {
      const deleted = await deleteFleetDocumentAPI(documentId, t);

      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination);
      }

      setOpenDialog(false);
      setDocumentId("");
    } catch (err) {
      console.error("Greška prilikom brisanja dokumenta:", err);
    }
  }, [documentId, id, t]);

  const handleDocumentDeleteDialogClose = useCallback(() => {
    setDocumentId("");
    setOpenDialog(false);
  }, []);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_FLEET_TIRE_SET_SEARCH.text,
      route: SIDEBAR_ITEM_FLEET_TIRE_SET_SEARCH.route,
    },
  ];

  if (!id) return <></>;

  const tireType = tireSet?.tires ? getTireType(tireSet.tires) : null;
  const dimension = tireSet?.tires ? getDimensionSet(tireSet.tires) : "";

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex items-center gap-4">
              {tireSet?.tires && (
                <div className="flex items-center justify-center w-[48px]">
                  {getTireIcon(tireSet.tires)}
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <PageTitle
                    title={tireSet?.code
                      ? `Set guma [${tireSet.code}${dimension ? ` · ${dimension}` : ""}${tireType?.name ? ` · ${tireType.name}` : ""}]`
                      : t(FLEET_TIRE_SET_DETAIL_PAGE_TITLE.text)}
                    icon={FLEET_TIRE_SET_DETAIL_PAGE_TITLE.icon}
                    iconColor={FLEET_TIRE_SET_DETAIL_PAGE_TITLE.iconColor}
                  />
                  {tireSet && (
                    <Badge
                      text={tireSet.isActive ? "Aktivan" : "Neaktivan"}
                      variant={tireSet.isActive ? "success" : "warning"}
                      showDot
                    />
                  )}
                  {tireSet?.isWrittenOff && (
                    <Badge text="Otpisano" variant="danger" showDot />
                  )}
                </div>
                <Breadcrumb items={breadcrumbs} />
              </div>
            </div>
          </PageHeader>
          <TabMenu tireSetId={id} />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <TireSetDocumentSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite dokument klikom na 'Dodaj dokument'" />
          ) : (
            <TireSetDocumentSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title="Brisanje dokumenta"
        message="Jeste li sigurni da želite obrisati ovaj dokument?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDocumentDeleteDialogConfirm}
        onCancel={handleDocumentDeleteDialogClose}
      />
    </>
  );
};

TireSetDocumentSearchPage.route =
  FleetTireRoutes.FleetTireSetDocumentSearchPageRouter;

export default TireSetDocumentSearchPage;
